import * as React from "react";
import { setDisplayTimeZone } from "./format";
import { AuthClient, isUnauthorized, type WhoAmI } from "@ozpanel/shared";

// AuthProvider — the session state every v2 app wraps itself in, built on
// @ozpanel/shared's AuthClient (S7.0/G47: the same session-cookie mechanism every v2 app
// uses, not a per-app shim). Lives in @ozpanel/ui (not @ozpanel/shared, which is
// framework-agnostic) because this IS a React binding — the second app to need it
// (app/admin, after app/mailbox) is exactly the "second occurrence" this codebase's own
// rule treats as the signal to extract rather than duplicate.

export interface AuthContextValue {
  client: AuthClient;
  who: WhoAmI | null;
  loading: boolean;
  /**
   * Sign in. Resolves "2fa_required" when the account has a second factor — the caller then
   * collects a code and calls verifyTwoFactor. It used to THROW "2FA is not yet supported in
   * v2 yet", which meant anyone who enabled a second factor could no longer sign in to any v2
   * console at all — using a control the panel itself offers. A setting that bricks the account
   * is worse than a missing feature.
   */
  login(email: string, password: string): Promise<"active" | "2fa_required">;
  /** Promote a pending session with a TOTP code or a recovery code. */
  verifyTwoFactor(code: string): Promise<void>;
  logout(): Promise<void>;
  stopImpersonating(): Promise<void>;
  /** Re-read the session. Call after anything that changes the principal server-side. */
  refresh(): Promise<void>;
  /** True when this session is an operator acting as someone else. */
  impersonating: boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [client] = React.useState(() => new AuthClient());
  const [who, setWho] = React.useState<WhoAmI | null>(null);
  const [loading, setLoading] = React.useState(true);
  // The session names the zone this login's times are shown in (A5 / P9); UTC until it is read.
  // Set during render (idempotent), so every child rendered with this session already uses it.
  setDisplayTimeZone(who?.time_zone);

  React.useEffect(() => {
    let cancelled = false;
    client
      .whoami()
      .then((w) => {
        if (!cancelled) setWho(w);
      })
      .catch((err) => {
        if (!cancelled && !isUnauthorized(err)) {
          // Something other than "not logged in" — surfaced to the console for now; a real
          // error banner is a later screen, not this pass's scope.
          console.error("whoami failed:", err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const login = React.useCallback(
    async (email: string, password: string) => {
      const result = await client.login(email, password);
      if (result.status === "2fa_required") {
        // The session exists and is PENDING. Reported to the caller rather than thrown, so the
        // login screen can ask for the code; whoami is deliberately not called here, because a
        // pending session is not yet an identity.
        return "2fa_required" as const;
      }
      setWho(await client.whoami());
      return "active" as const;
    },
    [client],
  );

  const verifyTwoFactor = React.useCallback(
    async (code: string) => {
      // Accepts a TOTP code OR a recovery code — the server decides which, and a person locked
      // out of their authenticator must not have to know they are on a different path.
      await client.apiFetch<unknown>("/auth/2fa/verify", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setWho(await client.whoami());
    },
    [client],
  );

  const refresh = React.useCallback(async () => {
    setWho(await client.whoami());
  }, [client]);

  const stopImpersonating = React.useCallback(async () => {
    await client.stopImpersonating();
    setWho(await client.whoami());
  }, [client]);

  const logout = React.useCallback(async () => {
    await client.logout();
    setWho(null);
  }, [client]);

  const value = React.useMemo(
    () => ({
      client,
      who,
      loading,
      login,
      verifyTwoFactor,
      logout,
      stopImpersonating,
      refresh,
      impersonating: !!who?.impersonated_by,
    }),
    [client, who, loading, login, verifyTwoFactor, logout, stopImpersonating, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
